/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";
import config from "../app.config";

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
    names: {
        signInUser: config.msalB2cSignInFlow, //'B2C_1_SignIn',
        signInStaff: config.msalB2cSignInStaffFlow, //'B2C_1_SignIn',
        forgotPassword: config.msalB2cResetPasswordFlow, //'B2C_1_PasswordRest',
        // editProfile: 'B2C_1_ProfileEdit',
    },
    authorities: {
        signInUser: {
            authority: `${config.msalB2cAuthority}/${config.msalB2cSignInFlow}` //'https://srxportal.b2clogin.com/srxportal.onmicrosoft.com/B2C_1_SignIn',
        },
        signInStaff: {
            authority: `${config.msalB2cAuthority}/${config.msalB2cSignInStaffFlow}` //'https://srxportal.b2clogin.com/srxportal.onmicrosoft.com/B2C_1_SignIn',
        },
        forgotPassword: {
            authority: `${config.msalB2cAuthority}/${config.msalB2cResetPasswordFlow}` //'https://srxportal.b2clogin.com/srxportal.onmicrosoft.com/B2C_1_PasswordRest',
        },
        // editProfile: {
        //     authority: 'https://srxportal.b2clogin.com/srxportal.onmicrosoft.com/B2C_1_ProfileEdit',
        // },
    },
    authorityDomain: config.msalB2cAuthorityDomain, //'srxportal.b2clogin.com',
};


/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */
export const msalConfig = {
    auth: {
        clientId: config.msalB2cClientId, //"ec0441a4-89ac-4604-a023-458a4582aa83", // This is the ONLY mandatory field that you need to supply.
        authority: b2cPolicies.authorities.signInUser.authority, // Choose SUSI as your default authority.
        knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
        redirectUri: config.returnUrl, //'http://localhost:3000/Dashboard', // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
        postLogoutRedirectUri: config.logoutUrl, //'https://z-srx-dev-app-portal.azurewebsites.net', // Indicates the page to navigate after logout.
        navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }
            },
        },
    },
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
    portalApiUser: {
        endpoint: `${config.returnUrl}`, //'https://localhost:6001/Dashboard',
        scopes: {
            access_user: [`${config.msalB2cTokenScopeUrl}/${config.msalB2cApiName}/${config.msalUserApi}`], // ['https://srxportal.onmicrosoft.com/PortalApi/access.user']
        },
    },
    portalApiStaff: {
        endpoint: `${config.returnUrl}`, //'https://localhost:6001/Dashboard',
        scopes: {
            access_staff: [`${config.msalB2cTokenScopeUrl}/${config.msalB2cApiName}/${config.msalStaffApi}`], // ['https://srxportal.onmicrosoft.com/PortalApi/access.staff']
        },
    },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
 export const loginRequestUser = {
    scopes: protectedResources.portalApiUser.scopes.access_user,
};

export const loginRequestStaff = {
    scopes: protectedResources.portalApiStaff.scopes.access_staff,
};
